import type { SurecoCsvDataType } from './constants'

// Rating area factor columns ra01–ra67 appear in PLANS_AND_RATES.
const RA_LABELS: Record<string, string> = Object.fromEntries(
  Array.from({ length: 67 }, (_, i) => {
    const n = (i + 1).toString().padStart(2, '0')
    return [`ra${n}`, `RA${n}`]
  })
)

/**
 * Explicit display-label map for every known fieldCompare field key per
 * SurecoCsvDataType. Derived from the EPS backend's
 * plan-management-v2-csv-parser.ts columns (the [csvHeader, camelCaseKey]
 * tuples, sourced from plan-management-v2-csv-templates.dto.ts).
 *
 * To update when the backend adds or renames a CSV column, add/change the
 * entry here — no logic files need touching.
 */
export const COMPARE_COLUMN_LABELS: Record<
  SurecoCsvDataType,
  Record<string, string>
> = {
  LOCATION: {
    year: 'Year',
    state: 'State',
    county: 'County',
    fipsCode: 'FIPS Code',
    zipCode: 'ZIP Code',
    ratingArea: 'Rating Area',
  },

  SMOKER_RATES: {
    year: 'Year',
    state: 'State',
    age: 'Age',
    issuerId: 'Issuer ID',
    increaseRate: 'Increase Rate',
    isActive: 'Active',
  },

  SERVICE_AREAS: {
    year: 'Year',
    state: 'State',
    issuerId: 'Issuer ID',
    sourceName: 'Source Name',
    hiosPlanId: 'HIOS Plan ID',
    serviceAreaId: 'Service Area ID',
    serviceAreaName: 'Service Area Name',
    coverEntireState: 'Cover Entire State',
    countyCode: 'County Code',
    partialCounty: 'Partial County',
    zipCode: 'ZIP Code',
    isActive: 'Active',
  },

  PLANS_AND_RATES: {
    year: 'Year',
    state: 'State',
    hiosPlanId: 'HIOS Plan ID',
    carrier: 'Carrier',
    planName: 'Plan Name',
    householdName: 'Household Name',
    planType: 'Plan Type',
    metal: 'Metal Level',
    isHsa: 'HSA',
    providerNetwork: 'Network Name',
    onExchange: 'On Exchange',
    offExchange: 'Off Exchange',
    isForSale: 'For Sale',
    proofOfResidenceRequired: 'Proof of Residence Required',
    serviceAreaId: 'Service Area ID',
    service_area_id: 'Service Area ID',
    baseRate: 'Base Rate',
    planFee: 'Plan Fee',
    planStatus: 'Plan Status',
    crosswalkHiosId: 'Crosswalk Plan ID',
    actuarialValue: 'Actuarial Value',
    ...RA_LABELS,
  },

  PLAN_BENEFITS: {
    year: 'Year',
    state: 'State',
    hios: 'HIOS',
    hiosPlanId: 'HIOS Plan ID',
    benefit: 'Benefit',
    serviceNotCovered: 'Service Not Covered',
    appliesToDeductible: 'Applies To Deductible',
    coinsurance: 'Coinsurance',
    copayAmount: 'Copay Amount',
    copayDayLimit: 'Copay Day Limit',
    coinsuranceCopayOrder: 'Coinsurance Copay Order',
    memberServicePaidCap: 'Member Service Paid Cap',
    coverageVisitLimit: 'Coverage Visit Limit',
    firstDollarVisits: 'First Dollar Visits',
    isGrouped: 'Is Grouped',
    copayAfterFirstVisits: 'Copay After First Visits',
    notes: 'Notes',
    benefitDisplayValue: 'Benefit Display Value',
    sort: 'Sort',
  },
}

/**
 * Returns the explicit display label for a field key in the given data type,
 * or undefined if the key is not yet in the map (caller should fall back to
 * the generic formatter).
 */
export function getColumnLabel(
  dataType: SurecoCsvDataType,
  key: string
): string | undefined {
  return COMPARE_COLUMN_LABELS[dataType]?.[key]
}

/**
 * Fields to exclude from the compare table per data type.
 * These are redundant or internal fields returned by the backend.
 */
const HIDDEN_COLUMNS: Partial<Record<SurecoCsvDataType, Set<string>>> = {
  PLANS_AND_RATES: new Set(['exchange', 'group']),
}

export function getHiddenColumns(dataType: SurecoCsvDataType): Set<string> {
  return HIDDEN_COLUMNS[dataType] ?? new Set()
}

/**
 * Returns the canonical column order for a data type, derived from the key
 * order of COMPARE_COLUMN_LABELS. Keys not in the map are appended at the end.
 */
export function getColumnOrder(dataType: SurecoCsvDataType): string[] {
  return Object.keys(COMPARE_COLUMN_LABELS[dataType] ?? {})
}

/**
 * Drops hidden columns and puts the rest in canonical order; keys not in the
 * label map keep their original order at the end.
 */
export function getVisibleColumnKeys(
  dataType: SurecoCsvDataType,
  rawKeys: string[]
): string[] {
  const hidden = getHiddenColumns(dataType)
  const visibleKeys = rawKeys.filter((k) => !hidden.has(k))
  const order = getColumnOrder(dataType)
  if (order.length === 0) return visibleKeys
  const knownSet = new Set(order)
  return [
    ...order.filter((k) => visibleKeys.includes(k)),
    ...visibleKeys.filter((k) => !knownSet.has(k)),
  ]
}
